/**
 * Azure Blob Storage — Chunked, Resumable, Parallel Upload
 * =========================================================
 *
 * Production-grade Block Blob uploader for GB-scale files.
 *
 *  Pipeline:
 *    [chunking] → [parallel block uploads w/ retry] → [pre-commit validation]
 *               → [Put Block List commit] → [byte-integrity callback]
 *
 *  Resumability:
 *    Per-file state is persisted to localStorage keyed by a deterministic
 *    fingerprint (name + size + lastModified). When the SAME File is uploaded
 *    again — even after a browser crash, refresh, or new tab — completed
 *    block indices are loaded and re-upload is skipped.
 *
 *    Azure retains uncommitted blocks for 7 days, so the resume window is
 *    bounded by that and by the SAS expiry (240 min). The caller supplies a
 *    `resolveSasUrl()` async function that can mint a fresh SAS for the
 *    blob_path when the original URL has expired.
 *
 *  Module layout (separation of concerns):
 *    - chunking      → fileFingerprint, encodeBlockId, sliceFile
 *    - state         → loadResumeState / saveResumeState / clearResumeState
 *    - retry         → withExponentialBackoff
 *    - upload        → putOneBlock, commitBlockList
 *    - orchestration → uploadFileInChunks (public entry)
 *
 *  Public surface:
 *    uploadFileInChunks(file, sasUrl, opts) → Promise<void>
 *      opts.onProgress({ pct, bytesUploaded, totalBytes, mbPerSec, etaSec })
 *      opts.resolveSasUrl?: () => Promise<string>   // for SAS rotation on resume
 *      opts.signal?: AbortSignal                    // optional cancellation
 */

// ---------- Tunables ----------
const BLOCK_SIZE = 4 * 1024 * 1024;        // 4 MB
const BLOCK_PARALLELISM = 2;               // Per-file in-flight blocks. The global
                                            // semaphore (below) is the actual ceiling
                                            // across a multi-file batch.
const BATCH_GLOBAL_INFLIGHT = 6;            // Max total in-flight PUTs across ALL files
                                            // in the same batch. Prevents the 23×2=46
                                            // PUT storm that triggered Azure account-
                                            // level throttling and "block-XXXX network
                                            // error" cascades reported by users.
const BLOCK_MAX_RETRIES = 6;               // Was 3. With backoff cap at 32s, total retry window is ~63s
                                            // per block before giving up — survives most ISP hiccups.
const COMMIT_MAX_RETRIES = 5;
const PER_BLOCK_TIMEOUT_MS = 180 * 1000;    // 3 min — generous for slow last-mile networks
                                            // uploading 4MB blocks of large pptx files.
const RESUME_LS_PREFIX = 'qv_chunked_upload_';

// ---------- Global semaphore (cross-file batch concurrency cap) ----------
//
// A single shared semaphore caps the TOTAL number of in-flight Azure PUTs
// across every file currently being uploaded. Without this, a batch of 23
// files at PARALLELISM=2 would burst 46 simultaneous PUTs to Azure, which
// (a) hits per-account throttling on Azure's side and (b) saturates the
// browser's per-origin connection pool, manifesting as "block-XXXX network
// error" cascades. With a cap of 6 in flight, throughput is still healthy
// (~24 MB in flight) and Azure is comfortably under throttle thresholds.

function makeSemaphore(maxConcurrent) {
  let active = 0;
  const waiters = [];
  return {
    async acquire() {
      if (active < maxConcurrent) {
        active++;
        return;
      }
      await new Promise((resolve) => waiters.push(resolve));
      active++;
    },
    release() {
      active--;
      const next = waiters.shift();
      if (next) next();
    },
  };
}

const __batchSemaphore = makeSemaphore(BATCH_GLOBAL_INFLIGHT);

// ---------- Chunking ----------

export function fileFingerprint(file) {
  // Deterministic per-file ID: name + size + lastModified.
  // (Different file with the same name → different fingerprint.)
  const raw = `${file.name}|${file.size}|${file.lastModified}`;
  // Stable hash without crypto module — bounded length, collision-safe enough
  // for a single user's local resume cache.
  let h = 5381;
  for (let i = 0; i < raw.length; i++) {
    h = ((h << 5) + h) + raw.charCodeAt(i);
    h |= 0;
  }
  return `${file.size}_${(h >>> 0).toString(36)}`;
}

export function encodeBlockId(index) {
  // Azure REQUIRES all block IDs in a list to be the same length.
  // "block-NNNNNN" → exactly 12 chars → fixed base64 length.
  const raw = `block-${String(index).padStart(6, '0')}`;
  return btoa(raw);
}

function sliceFile(file, index) {
  const start = index * BLOCK_SIZE;
  const end = Math.min(start + BLOCK_SIZE, file.size);
  return file.slice(start, end);
}

// ---------- Resume state (localStorage) ----------

function lsKey(fp) { return RESUME_LS_PREFIX + fp; }

export function loadResumeState(fp) {
  try {
    const raw = localStorage.getItem(lsKey(fp));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !Array.isArray(obj.uploadedIdxs)) return null;
    return obj;
  } catch (e) {
    return null;
  }
}

function saveResumeState(fp, state) {
  try {
    localStorage.setItem(lsKey(fp), JSON.stringify(state));
  } catch (e) {
    // QuotaExceeded etc — non-fatal, just disables resume for this run.
  }
}

export function clearResumeState(fp) {
  try { localStorage.removeItem(lsKey(fp)); } catch (e) { /* noop */ }
}

// ---------- Retry ----------

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function withExponentialBackoff(fn, label, maxRetries) {
  let lastErr;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastErr = err;
      // Always log so DevTools shows the precise pattern (block index + reason).
      // eslint-disable-next-line no-console
      console.error(
        `[chunked-upload] ${label} attempt ${attempt}/${maxRetries} FAILED: ${err.message || err}`
      );
      if (attempt === maxRetries) break;
      // Exponential backoff capped at 32s: 1s, 2s, 4s, 8s, 16s, 32s, 32s ...
      const wait = Math.min(32000, Math.pow(2, attempt - 1) * 1000);
      await sleep(wait);
    }
  }
  throw lastErr;
}

// ---------- Single block PUT ----------

function putOneBlockOnce(sasUrl, blockId, chunk, onBytes, signal) {
  return new Promise((resolve, reject) => {
    const url = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;
    const xhr = new XMLHttpRequest();
    let lastSent = 0;
    if (signal) {
      const abort = () => { try { xhr.abort(); } catch (e) {} reject(new Error('Aborted')); };
      if (signal.aborted) return abort();
      signal.addEventListener('abort', abort, { once: true });
    }
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onBytes) {
        const delta = ev.loaded - lastSent;
        lastSent = ev.loaded;
        if (delta > 0) onBytes(delta);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`block ${blockId} HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error(`block ${blockId} network error`));
    xhr.ontimeout = () => reject(new Error(`block ${blockId} timeout (${PER_BLOCK_TIMEOUT_MS}ms)`));
    xhr.open('PUT', url);
    xhr.timeout = PER_BLOCK_TIMEOUT_MS;
    xhr.send(chunk);
  });
}

async function putOneBlock(sasUrl, blockId, chunk, onBytes, signal) {
  // Per-block retry. Progress accounting subtracts already-counted bytes on
  // retry so the running total stays consistent. The global batch semaphore
  // caps how many of these are in flight at the SAME time across all files
  // currently being uploaded by this tab — preventing the 23×2=46 PUT storm.
  let countedThisAttempt = 0;
  const wrappedOnBytes = (delta) => {
    countedThisAttempt += delta;
    if (onBytes) onBytes(delta);
  };
  await __batchSemaphore.acquire();
  try {
    await withExponentialBackoff(async () => {
      if (countedThisAttempt > 0) {
        // Reset progress for this block on retry to prevent over-counting.
        if (onBytes) onBytes(-countedThisAttempt);
        countedThisAttempt = 0;
      }
      await putOneBlockOnce(sasUrl, blockId, chunk, wrappedOnBytes, signal);
    }, `block ${blockId}`, BLOCK_MAX_RETRIES);
  } finally {
    __batchSemaphore.release();
  }
}

// ---------- Commit (Put Block List) ----------

async function commitBlockListOnce(sasUrl, blockIds) {
  const xml =
    '<?xml version="1.0" encoding="utf-8"?><BlockList>' +
    blockIds.map((id) => `<Latest>${id}</Latest>`).join('') +
    '</BlockList>';
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`commit HTTP ${xhr.status}: ${xhr.responseText || ''}`));
    };
    xhr.onerror = () => reject(new Error('commit network error'));
    xhr.open('PUT', `${sasUrl}&comp=blocklist`);
    xhr.setRequestHeader('Content-Type', 'application/xml');
    xhr.send(xml);
  });
}

async function commitBlockList(sasUrl, blockIds) {
  await withExponentialBackoff(
    () => commitBlockListOnce(sasUrl, blockIds),
    'commit',
    COMMIT_MAX_RETRIES,
  );
}

// ---------- Speed / ETA ----------

function makeSpeedTracker() {
  // Rolling 5-second window of (timestamp, bytes) samples.
  const samples = [];
  return {
    record(bytes) {
      const now = performance.now();
      samples.push([now, bytes]);
      // Drop samples older than 5s
      const cutoff = now - 5000;
      while (samples.length && samples[0][0] < cutoff) samples.shift();
    },
    mbPerSec() {
      if (samples.length < 2) return 0;
      const [t0, b0] = samples[0];
      const [t1, b1] = samples[samples.length - 1];
      const dt = (t1 - t0) / 1000;
      if (dt <= 0) return 0;
      const bytesInWindow = samples.reduce((acc, [, b]) => acc + b, 0) - samples[0][1];
      return bytesInWindow / dt / (1024 * 1024);
    },
  };
}

// ---------- Public orchestration ----------

export async function uploadFileInChunks(file, sasUrl, opts = {}) {
  const { onProgress, resolveSasUrl, signal } = opts;

  const totalBlocks = Math.max(1, Math.ceil(file.size / BLOCK_SIZE));
  const totalBytes = Math.max(1, file.size);
  const fp = fileFingerprint(file);

  // Build the canonical block ID list (order matters for Put Block List)
  const blockIds = [];
  for (let i = 0; i < totalBlocks; i++) blockIds.push(encodeBlockId(i));

  // ---- Resume detection ----
  const prior = loadResumeState(fp);
  const completed = new Set(
    prior && prior.totalBlocks === totalBlocks ? prior.uploadedIdxs : []
  );

  let bytesUploaded = completed.size * BLOCK_SIZE;
  // Last block can be partial; clamp on resume
  if (bytesUploaded > totalBytes) bytesUploaded = totalBytes;

  const speed = makeSpeedTracker();
  let lastProgressEmit = 0;

  const emitProgress = () => {
    const now = performance.now();
    if (now - lastProgressEmit < 200 && bytesUploaded < totalBytes) return;
    lastProgressEmit = now;
    if (!onProgress) return;
    const mbps = speed.mbPerSec();
    const remaining = totalBytes - bytesUploaded;
    const etaSec = mbps > 0.01 ? Math.round(remaining / (mbps * 1024 * 1024)) : null;
    // Reserve final 5% of the 0..100 scale for the commit phase
    const pct = Math.min(95, Math.round((bytesUploaded / totalBytes) * 95));
    onProgress({ pct, bytesUploaded, totalBytes, mbPerSec: mbps, etaSec });
  };

  const onChunkBytes = (delta) => {
    bytesUploaded += delta;
    if (delta > 0) speed.record(delta);
    emitProgress();
  };

  emitProgress();

  // ---- Resolve SAS (rotate if caller provides a refresher) ----
  let activeSas = sasUrl;

  // ---- Worker pool ----
  let nextIdx = 0;
  let sasRotationLock = null;  // single-flight: only one worker at a time may refresh
  const rotateSasOnce = async (reason) => {
    if (!resolveSasUrl) return;
    if (sasRotationLock) return sasRotationLock;
    sasRotationLock = (async () => {
      try {
        // eslint-disable-next-line no-console
        console.warn(`[chunked-upload] rotating SAS (${reason})`);
        activeSas = await resolveSasUrl();
      } finally {
        // Hold lock for 2s so concurrent workers piggyback on this rotation
        // instead of stampeding the resume endpoint.
        setTimeout(() => { sasRotationLock = null; }, 2000);
      }
    })();
    return sasRotationLock;
  };

  const worker = async () => {
    while (true) {
      if (signal && signal.aborted) throw new Error('Aborted');
      const i = nextIdx++;
      if (i >= totalBlocks) return;
      if (completed.has(i)) continue;
      try {
        await putOneBlock(activeSas, blockIds[i], sliceFile(file, i), onChunkBytes, signal);
      } catch (err) {
        // Persistent failure across BLOCK_MAX_RETRIES. Before truly giving up
        // on this block, force-rotate the SAS once and retry one more time.
        // This catches the "stale SAS reads as network error via CORS preflight"
        // failure mode that no amount of vanilla retry will fix.
        const msg = err.message || '';
        const isAuth = /403|401|AuthenticationFailed|ExpiredSas/i.test(msg);
        const isNet  = /network error|timeout|Failed to fetch|NetworkError/i.test(msg);
        if (resolveSasUrl && (isAuth || isNet)) {
          await rotateSasOnce(isAuth ? 'auth-failure' : 'network-failure');
          // One last try with the fresh SAS — independent of block retry budget.
          await putOneBlock(activeSas, blockIds[i], sliceFile(file, i), onChunkBytes, signal);
        } else {
          throw err;
        }
      }
      completed.add(i);
      saveResumeState(fp, {
        fingerprint: fp,
        totalBlocks,
        uploadedIdxs: Array.from(completed),
        savedAt: Date.now(),
      });
    }
  };

  const workers = [];
  for (let w = 0; w < Math.min(BLOCK_PARALLELISM, totalBlocks); w++) workers.push(worker());
  await Promise.all(workers);

  // ---- Pre-commit validation ----
  if (completed.size !== totalBlocks) {
    throw new Error(
      `Pre-commit validation failed: ${completed.size}/${totalBlocks} blocks uploaded`,
    );
  }

  // ---- Commit ----
  await commitBlockList(activeSas, blockIds);

  // ---- Cleanup resume state ----
  clearResumeState(fp);

  if (onProgress) {
    onProgress({
      pct: 100,
      bytesUploaded: totalBytes,
      totalBytes,
      mbPerSec: speed.mbPerSec(),
      etaSec: 0,
    });
  }
}

// ---------- Misc helpers exposed for UI ----------

export function formatBytesPerSecond(mbps) {
  if (!mbps || mbps < 0.01) return '—';
  if (mbps >= 1) return `${mbps.toFixed(1)} MB/s`;
  return `${(mbps * 1024).toFixed(0)} KB/s`;
}

export function formatEta(sec) {
  if (sec == null || !isFinite(sec)) return '—';
  if (sec < 60) return `${Math.max(1, sec)}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
